import React, { useState } from 'react';
import { images } from '../../constants/images';
import Button from '../button/Button';
import PricingCard from '../PricingCard';
import { Icons } from '../../assets/SVG-Icons';

type PlanKey = 'Pro' | 'Premium' | 'Business+' | 'Enterprise';

type PlanType = {
  setupFee: string;
  previousSetupFee?: string;
  monthlyFee: number | string;
  monthlyFeeText?: string;
  buttonTitle?: string;
  isMostPopular?: boolean;
  features: {
    domain: string;
    referralLinkAlias: string;
    SingleSignOnIntegration?: boolean;
    CustomAPIsAndIntegrations?: boolean;
    countriesLanguages: string;
    forUpEnrolledCards: string | null;
    enrolled: { cards: string; additionalCards: string } | string;
    newsletters: string;
    support: string;
    cashbackFrequency?: string;
    standoutFeatures?: string | JSX.Element;
    appMode: string;
  };
  bannerText?: string;
};

const pricingPlans: Record<PlanKey, PlanType> = {
  'Pro': {
    setupFee: '4,995',
    monthlyFee: '250',
    features: {
      domain: 'Yourbrand.Saversapp.com',
      referralLinkAlias: '5-digit random code',
      countriesLanguages: 'USA \n English',
      forUpEnrolledCards: '1,000',
      enrolled: {
        cards: '1,000',
        additionalCards: '1,000',
      },
      newsletters: '2 / week',
      appMode: 'Co-branded App',
      support: 'Email Support',
    },
    bannerText: 'Free Upgrade to Business+',
  },
  'Premium': {
    previousSetupFee: '9,995',
    setupFee: '4,995',
    monthlyFee: '250',
    features: {
      domain: 'Yourbrand.Saversapp.com',
      referralLinkAlias: '5-digit random code',

      countriesLanguages: 'USA \n English & Spanish*',
      appMode: 'Co-branded App',
      forUpEnrolledCards: '2,500',
      enrolled: {
        cards: '2,500',
        additionalCards: '2,500',
      },
      newsletters: '4 / week',
      support: 'Email Support',
    },
    bannerText: 'Free Upgrade to Business+',
  },
  'Business+': {
    setupFee: '4,995',
    previousSetupFee: '14,995',
    monthlyFee: '250',
    isMostPopular: true,
    buttonTitle: 'Get Started',
    features: {
      domain: 'Yourbrand.Saversapp.com',
      referralLinkAlias: 'Vanity Code (brand name)',

      countriesLanguages: 'USA & Canada* \nEnglish, Spanish* & French*',
      appMode: 'Co-branded App',
      forUpEnrolledCards: '5,000',
      enrolled: {
        cards: '5,000',
        additionalCards: '5,000',
      },
      newsletters: 'Daily',
      support: 'Dedicated Account Manager',
    },
    bannerText: 'Limited Time Offer',
  },
  'Enterprise': {
    setupFee: '4,995',
    previousSetupFee: '14,995',
    monthlyFee: '',
    monthlyFeeText:
      'Subject to prequalification. Minimum MAU (Monthly Active Users) of 50,000+',
    buttonTitle: 'Get Started',
    features: {
      domain: 'www.your.domain',
      referralLinkAlias: 'Vanity Code (brand name)',
      SingleSignOnIntegration: true,
      CustomAPIsAndIntegrations: true,
      countriesLanguages: 'USA & Canada* \nEnglish, Spanish* & French*',
      forUpEnrolledCards:
        'Integration/dev, if any, to be scoped and priced separately.',
      enrolled: {
        cards: '5,000',
        additionalCards: '5,000',
      },
      newsletters: 'Custom',
      appMode: 'Co-branded App or Integrated In Existing App',
      support: 'Dedicated Account Manager',
    },
    bannerText: 'Limited Time Offer',
  },
};

function PricingSection() {
  const [showFullFeatures, setShowFullFeatures] = useState(false);
  const [activeTab, setActiveTab] = useState('Pro');

  const planKey = activeTab as keyof typeof pricingPlans;

  const handleShowFeatures = () => {
    if (showFullFeatures)
      document.getElementById('pricing')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    setShowFullFeatures(!showFullFeatures);
  };
  return (
    <>
      <div
        className='bg-cover md:bg-cover md:bg-center hidden sm:block sm:bg-[#38c99f]'
        style={{
          backgroundImage: `url(${
            showFullFeatures ? images.PricingBG : images.PricingSmallBG
          })`,
        }}
      >
        <div className='container-fluid md:container md:text-center md:mx-auto md:py-[2.8rem] lg:px-14'>
          <p
            className='pl-1 text-white text-2xl md:text-[2.375rem] font-semibold md:font-bold sm:uppercase mt-6 md:mt-0'
            style={{ fontFamily: 'Mulish' }}
          >
            Pricing
          </p>
          <p
            className='hidden sm:block  text-white text-[1.25rem] md:text-base pt-3 pb-6 lg:pt-5 lg:pb-16 font-medium'
            style={{ fontFamily: 'Mulish' }}
          >
            Choose the best subscription for your business, with the flexibility
            to change as you grow.
          </p>
          {/* Cards */}
          <div className='grid grid-cols-2 xmd:grid-cols-3 xl:grid-cols-4 gap-6 justify-items-center'>
            {Object.keys(pricingPlans).map(planKey => {
              const plan = pricingPlans[planKey as keyof typeof pricingPlans];
              return (
                <PricingCard
                  key={planKey}
                  title={planKey}
                  buttonTitle={plan.buttonTitle || ''}
                  setupFee={plan.setupFee}
                  previousSetupFee={plan.previousSetupFee}
                  monthlyFee={plan.monthlyFee}
                  monthlyFeeText={plan.monthlyFeeText}
                  features={plan.features as any}
                  showFullFeatures={showFullFeatures}
                  isMostPopular={plan.isMostPopular}
                  bannerText={plan.bannerText}
                />
              );
            })}
          </div>

          <Button
            primary
            className='py-4 my-8 md:mb-0 mt-12 md:mx-auto w-[17.3rem] h-[3.75rem] flex gap-4'
            // onClick={() => navigate('/signup')}
            onClick={handleShowFeatures}
          >
            {showFullFeatures ? (
              <p className='flex gap-4'>
                Hide list of features{Icons.upArrow2()}
              </p>
            ) : (
              <p className='flex gap-4'>
                Full list of features{Icons.downArrow2()}
              </p>
            )}
          </Button>
          <div className='text-center mt-6 space-y-2 text-white text-lg'>
            <p>
              Charity & Non-Profit Organizations get{' '}
              <span className='font-semibold'>50% off</span> the Set-Up Fee
            </p>
            <p>
              * Spanish language, French language, and Savers App Canada are
              scheduled for the first half of 2026
            </p>
          </div>
        </div>
      </div>
      {/* Pricing For Mobile */}
      <div className='block sm:hidden'>
        <div className='bg-[#F6F6F6] py-1'>
          <div className='flex flex-col gap-3 px-5 py-6'>
            <h2 className='text-2xl font-bold text-left sm:mb-4'>Pricing</h2>
            <p className='font-normal text-base text-black'>
              Choose the best subscription for your business, with the
              flexibility to change as you grow.
            </p>
          </div>
          <div className='bg-[#f6f6f6]'>
            <div className='grid grid-cols-4 w-full border-b border-[#F6F6F6s] gap-[1px]'>
              {Object.keys(pricingPlans).map(tab => (
                <button
                  key={tab}
                  className={`py-2 px-1 xs:px-4 text-xs xs:text-sm font-bold border-t-2 transition-colors duration-300 ${
                    activeTab === tab
                      ? 'text-black border-primary'
                      : 'text-gray-500 border-transparent bg-white'
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <PricingCard
            title={activeTab}
            setupFee={pricingPlans[planKey].setupFee}
            previousSetupFee={pricingPlans[planKey].previousSetupFee}
            monthlyFeeText={pricingPlans[planKey].monthlyFeeText}
            monthlyFee={pricingPlans[planKey].monthlyFee}
            features={pricingPlans[planKey].features as any}
            showFullFeatures={showFullFeatures}
            isMostPopular={pricingPlans[planKey].isMostPopular}
            buttonTitle={pricingPlans[planKey].buttonTitle || ''}
            bannerText={pricingPlans[planKey].bannerText}
          />

          <Button
            primary
            className='py-4  my-6 md:mx-auto w-10/12 h-[3.75rem] mx-auto'
            // onClick={() => navigate('/signup')}
            onClick={handleShowFeatures}
          >
            <span>
              {showFullFeatures ? (
                <p className='flex gap-4'>
                  Hide list of features{Icons.upArrow2()}
                </p>
              ) : (
                <p className='flex gap-4'>
                  Full list of features{Icons.downArrow2()}
                </p>
              )}
            </span>
          </Button>
          <div className='text-center mt-6 space-y-2 text-gray-600 text-lg'>
            <p>
              Charity & Non-Profit Organizations get{' '}
              <span className='font-semibold'>50% off</span> the Set-Up Fee
            </p>
            <p>
              Spanish language, French language, and Savers App Canada are
              scheduled for the first half of 2026
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default PricingSection;
